import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import theme from '@/styles/theme';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Modal from '@/components/common/common-modal';
import Message from '@/constant/message';
import { EAuthModalPage } from '@/store/types';
import { actions } from '@/store';
import useSelectorUser from '@/hooks/use-selector-user';
import useAlert from '@/hooks/use-alert';
import postCancelUnregistMember from '@/fetches/user/auth/post-cancel-unregist-member';
import dayjs from 'dayjs';
import media from '@/styles/media';
import timeSleep from '@/utils/time-sleep';
import LoadingSpinner from '@/components/pages/user/common/loading-spinner';
import { HeaderButtonDiv } from '../select-user';
import { BasicButton, BasicButtonLabel } from '../common/new-auth-button';
import { EmailInput } from '../common/new-auth-input';
import {
  Logo, MainDiv, UpperDiv, Wrapper,
} from '../common/new-auth-layout';
import MobileLogo from '../common/auth-layout/mobile-logo';

export const WillBeDeletedAccount = () => {
  const { deletedAt, email } = useSelectorUser('authModal');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { alert } = useAlert();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const gotoLogin = useCallback(() => {
    dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.INPUT_EMAIL));
  }, [dispatch]);

  const formatedDeletedAt = useMemo(() => dayjs(deletedAt).format('YYYY. MM. DD.'), [deletedAt]);
  const onClickGoBackButton = useCallback(() => {
    dispatch(actions.user.authModal.init());
    dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.INPUT_EMAIL));
  }, [dispatch]);

  const onClickCancelUnregistButton = useCallback(async () => {
    setIsLoading(true);
    const res = await postCancelUnregistMember(email);
    if (res) {
      await alert(t(Message.계정탈퇴가취소되었습니다다시로그인해주세요));
      onClickGoBackButton();
    } else {
      await alert(t(Message.처리도중서버에서오류가발생했습니다잠시후다시시도하시거나관리자에게문의해주세요));
    }
    setIsLoading(false);
  }, [alert, email, onClickGoBackButton, t]);

  return (
    <Modal show closeOnClickOverlay={false} backgroundOpacity={1}>
      <Wrapper>
        <Container>
          <UpperDiv>
            <Logo src="/assets/images/common/STEAMuP-logo-v4.png" alt="logo" />
            <HeaderButtonDiv>
              <BasicButtonLabel>
                {`${t(Message.로그인화면으로가기)}`}
              </BasicButtonLabel>
              <BasicButton
                className="withdrawl"
                onClick={gotoLogin}
              >
                {t(Message.로그인)}
              </BasicButton>
            </HeaderButtonDiv>
          </UpperDiv>
          <Main>
            <MobileLogo />
            <MainUpper>
              <Title>{t(Message.계정탈퇴가진행중입니다)}</Title>
              <EmailInput disabled value={email} validate />
              <Info>
                {`${t(Message.삭제예정일)} : ${(formatedDeletedAt).toString()}`}
              </Info>
            </MainUpper>
            <MainLower>
              <Subtitle>{t(Message.안내)}</Subtitle>
              <TimeInfo>
                {/* {t(Message.해당사용자가탈퇴되어삭제대기중입니다삭제예정일전에탈퇴를취소하고계정을다시이용할수있습니다그러나삭제예정일이경과하면사용자정보가삭제되며다시복구할수없습니다)} */}
                <Row>
                  {t(Message.해당사용자가탈퇴되어삭제대기중입니다)}
                </Row>
                <Row>
                  {t(Message.삭제예정일전에탈퇴를취소하고계정을다시이용할수있습니다)}
                </Row>
                <Row>
                  {t(Message.그러나삭제예정일이경과하면사용자정보가삭제되며다시복구할수없습니다)}
                </Row>
              </TimeInfo>
              <ButtonContainer>
                <Button color="gray" onClick={onClickGoBackButton}>{t(Message.이전)}</Button>
                <Button
                  color="red"
                  onClick={onClickCancelUnregistButton}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <LoadingSpinner color={theme.color.warningRed} />
                  ) : t(Message.탈퇴취소)}
                </Button>
              </ButtonContainer>
            </MainLower>
          </Main>
        </Container>
      </Wrapper>
    </Modal>
  );
};

const Row = styled.div`
  
`;
const Container = styled(MainDiv)`
  display: flex;
  flex-direction: column;
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0px 490px;
  justify-content: center;
  align-items: center;
  @media(max-width: 1350px){
    padding: 0px 399px;
  }
  @media(max-width: 1100px){
    padding: 0px 350px;
  }
  @media(max-width: 1050px){
    padding: 0px 335px;
  }
  ${media.lg`
    padding: 0;
  `}
`;
const MainUpper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  border-bottom: 1px solid ${theme.color.border};
 
  ${media.lg`
    width: 90%;
  `}
  `;

const Title = styled.div`
  font-family: ${theme.fontFamily.bold};
  font-size: 22px;
  line-height: 26px;
  text-align: center;
  padding-bottom: 47px;
  ${media.lg`
    padding: 47px 0;
    font-size: 26px;
    line-height: 31px;
  `}
`;
const Subtitle = styled.div`
  font-family:  ${theme.fontFamily.semiBold};
  font-size: 18px;
  line-height: 21px;
  text-align: center;

`;

const TimeInfo = styled.div`
  font-family: ${theme.fontFamily.regular};
  font-size: 15px;
  line-height: 24px;
  text-align: center;
  padding: 32px 15px;
  white-space: pre-wrap;

  >div {
    padding: 0 18px;
  }
  ${media.lg`
    padding: 40px 18px;
  `}
  ${media.xs`
     padding: 40px 0px;
  `}
`;

const Info = styled(TimeInfo)`
  color: #DD3E3E;
  ${media.lg`
    line-height: 18px;
  `}
`;

const MainLower = styled.div`
  padding: 33px 0;
  ${media.lg`
    padding: 40px 0;
  `}
`;

const ButtonContainer = styled.div`
  display: flex;
  gap:14px;
  align-items:center ;
  justify-content: center;
  padding: 35px 50px;
  @media(max-width: 325px) {
    /* padding: 35px 47px; */
  }
`;

interface ButtonProps{
  color?: string;
}
const Button = styled(BasicButton)<ButtonProps>`
  width: 150px;
  padding: 12px;
  flex: 0.5;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => (props.color === 'gray' ? '#4B586A' : theme.color.warningRed)};
  :disabled{
    background-color: ${(props) => (props.color === 'gray' ? '#4B586A' : theme.color.warningRed)};
  }
`;
